import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import inititializeFirebase from "./firebase";

// Re-mint this many ms before the token's deadline so a request never goes
// out with a token that expires mid-flight.
const EXPIRY_SKEW_MS = 60_000;

let firebaseUser = null;
let session = null; // { accessToken, expiresAtMs }
let mintPromise = null; // in-flight mint, shared by concurrent callers
let listenerStarted = false;

/**
 * Watch Firebase auth lazily (first token request starts it) so importing
 * this module on pre-auth public pages has no side effects. When a rep signs
 * in we mint eagerly in the background, so the session is usually ready
 * before their first search.
 */
function ensureAuthListener() {
  if (listenerStarted) return;
  listenerStarted = true;
  const app = inititializeFirebase();
  onAuthStateChanged(getAuth(app), (user) => {
    firebaseUser = user;
    if (user) {
      mintSession().catch(() => {});
    } else {
      session = null;
      mintPromise = null;
    }
  });
}

function mintSession() {
  if (mintPromise) return mintPromise;
  const callable = httpsCallable(
    getFunctions(inititializeFirebase()),
    "users-mintSupabaseSession",
  );
  mintPromise = callable()
    .then(({ data }) => {
      session = {
        accessToken: data.accessToken,
        // Deadline from local clock + server-reported lifetime, so a wrong
        // client clock cannot produce a token we think is still valid.
        expiresAtMs: Date.now() + data.expiresIn * 1000,
      };
      return session.accessToken;
    })
    .catch((err) => {
      console.warn("Supabase session mint failed:", err?.message ?? err);
      session = null;
      return null;
    })
    .finally(() => {
      mintPromise = null;
    });
  return mintPromise;
}

function sessionTokenIfValid() {
  if (session && Date.now() < session.expiresAtMs - EXPIRY_SKEW_MS) {
    return session.accessToken;
  }
  return null;
}

/**
 * Returns a Supabase access token for the signed-in rep, or null when there
 * is no Firebase user or the mint fails — callers then proceed as anon,
 * which keeps the pre-auth fail-open paths (shareBridge.js, v1Lockout.js)
 * exactly as they are today.
 *
 * If an AbortSignal is supplied and fires while a mint is in flight, this
 * resolves null immediately instead of waiting the mint out, so
 * timeout-bounded callers stay timeout-bounded.
 */
export async function getSupabaseAccessToken({ signal, forceRefresh } = {}) {
  ensureAuthListener();
  if (!forceRefresh) {
    const valid = sessionTokenIfValid();
    if (valid) return valid;
  }
  if (!firebaseUser) return null;
  if (signal?.aborted) return null;

  const mint = mintSession();
  if (!signal) return mint;
  return Promise.race([
    mint,
    new Promise((resolve) => {
      signal.addEventListener("abort", () => resolve(null), { once: true });
    }),
  ]);
}

/** True when a signed-in rep exists, i.e. a 401/403 is worth a re-mint. */
export function hasFirebaseUser() {
  ensureAuthListener();
  return firebaseUser != null;
}
